import React, { useState } from 'react';
import './RoleManagement.css';

export type PermissionPreset = 'none' | 'view' | 'operate' | 'full';

export interface ModuleActions {
    readList: boolean;
    readDetail: boolean;
    create: boolean;
    update: boolean;
    delete: boolean;
    export: boolean;
    manageEmails?: boolean;
}

export interface SubMenuPermission {
    preset: PermissionPreset;
    actions: ModuleActions;
}

export interface Role {
    id: string;
    name: string;
    description: string;
    userCount: number;
    permissions: {
        management_fleet: SubMenuPermission;
        management_driver: SubMenuPermission;
        control_battery: SubMenuPermission;
        linkage_driving: SubMenuPermission;
        linkage_maintenance: SubMenuPermission;
        linkage_disposal: SubMenuPermission;
        repair_diagnosis: SubMenuPermission;
        repair_recommendation: SubMenuPermission;
        repair_request: SubMenuPermission;
        repair_status: SubMenuPermission;
    };
}

const getFullActions = (): ModuleActions => ({ readList: true, readDetail: true, create: true, update: true, delete: true, export: true, manageEmails: true });
const getOperateActions = (): ModuleActions => ({ readList: true, readDetail: true, create: true, update: true, delete: false, export: false, manageEmails: true });
const getViewActions = (): ModuleActions => ({ readList: true, readDetail: true, create: false, update: false, delete: false, export: false, manageEmails: false });
const getNoneActions = (): ModuleActions => ({ readList: false, readDetail: false, create: false, update: false, delete: false, export: false, manageEmails: false });

export const defaultRoles: Role[] = [
    {
        id: 'r1',
        name: '최고 관리자',
        description: '시스템 설정부터 데이터 삭제, 내보내기까지 제한 없이 모든 권한을 가진 관리자 그룹',
        userCount: 2,
        permissions: {
            management_fleet: { preset: 'full', actions: getFullActions() },
            management_driver: { preset: 'full', actions: getFullActions() },
            control_battery: { preset: 'full', actions: getFullActions() },
            linkage_driving: { preset: 'full', actions: getFullActions() },
            linkage_maintenance: { preset: 'full', actions: getFullActions() },
            linkage_disposal: { preset: 'full', actions: getFullActions() },
            repair_diagnosis: { preset: 'full', actions: getFullActions() },
            repair_recommendation: { preset: 'full', actions: getFullActions() },
            repair_request: { preset: 'full', actions: getFullActions() },
            repair_status: { preset: 'full', actions: getFullActions() }
        }
    },
    {
        id: 'r2',
        name: '모니터링 담당',
        description: '운행 기록 및 배터리 상태 등 현황만을 파악하는 그룹 (정보 수정 및 파일 다운로드 불가)',
        userCount: 8,
        permissions: {
            management_fleet: { preset: 'view', actions: getViewActions() },
            management_driver: { preset: 'view', actions: getViewActions() },
            control_battery: { preset: 'view', actions: getViewActions() },
            linkage_driving: { preset: 'view', actions: getViewActions() },
            linkage_maintenance: { preset: 'view', actions: getViewActions() },
            linkage_disposal: { preset: 'view', actions: getViewActions() },
            repair_diagnosis: { preset: 'view', actions: getViewActions() },
            repair_recommendation: { preset: 'view', actions: getViewActions() },
            repair_request: { preset: 'none', actions: getNoneActions() },
            repair_status: { preset: 'view', actions: getViewActions() }
        }
    },
    {
        id: 'r3',
        name: '운영 실무자',
        description: '차량 현장 정비 및 배차 업무 담당 (삭제나 엑셀 다운로드를 제외한 대부분의 실무 기능 사용 가능)',
        userCount: 15,
        permissions: {
            management_fleet: { preset: 'view', actions: getViewActions() },
            management_driver: { preset: 'view', actions: getViewActions() },
            control_battery: { preset: 'operate', actions: getOperateActions() },
            linkage_driving: { preset: 'operate', actions: getOperateActions() },
            linkage_maintenance: { preset: 'operate', actions: getOperateActions() },
            linkage_disposal: { preset: 'none', actions: getNoneActions() },
            repair_diagnosis: { preset: 'operate', actions: getOperateActions() },
            repair_recommendation: { preset: 'operate', actions: getOperateActions() },
            repair_request: { preset: 'operate', actions: getOperateActions() },
            repair_status: { preset: 'operate', actions: getOperateActions() }
        }
    }
];

const MODULE_GROUPS = [
    {
        tabName: '관리 메뉴 권한',
        subModules: [
            { 
                key: 'management_fleet', title: '법인 차량', desc: '지점별 보유 차량 확인, 차량 기초 정보 등록 및 수정',
                icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13"></rect><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon><circle cx="5.5" cy="18.5" r="2.5"></circle><circle cx="18.5" cy="18.5" r="2.5"></circle></svg>
            },
            { 
                key: 'management_driver', title: '운전자', desc: '소속된 운전자 명단 확인, 면허 정보 확인 및 배차 관리',
                icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
            }
        ]
    },
    {
        tabName: '관제 메뉴 권한',
        subModules: [
            { 
                key: 'control_battery', title: '실시간 배터리 모니터링', desc: '차량 별 실시간 배터리 상태 진단 분석 및 위험 알림 모니터링',
                icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>
            }
        ]
    },
    {
        tabName: '운영 메뉴 권한',
        subModules: [
            { 
                key: 'linkage_driving', title: '운행 기록 관리', desc: '차량별 이동 경로 확인 및 자동 생성된 운행일지 열람',
                icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"></polygon><line x1="9" y1="3" x2="9" y2="21"></line><line x1="15" y1="3" x2="15" y2="21"></line></svg>
            },
            { 
                key: 'linkage_maintenance', title: '사고 및 정비 이력', desc: '사고 및 유지보수 발생 내역을 수동으로 등록하고 점검 이력 확인',
                icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path></svg>
            },
            {
                key: 'linkage_disposal', title: '처분 자산 심사', desc: '성능 저하 차량에 대한 매각이나 배터리 교체 타당성 분석 및 의사결정',
                icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"></polyline><polyline points="1 20 1 14 7 14"></polyline><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg>
            }
        ]
    },
    {
        tabName: '정비 메뉴 권한',
        subModules: [
            {
                key: 'repair_diagnosis', title: '고장 진단', desc: '계기판 경고등 사진 업로드 및 고장 코드 자동 판독',
                icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line><line x1="11" y1="8" x2="11" y2="14"></line><line x1="8" y1="11" x2="14" y2="11"></line></svg>
            },
            {
                key: 'repair_recommendation', title: '정비소 추천', desc: '주변 트럭 정비 가능 업체 추천 및 예상 시간·비용 안내',
                icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
            },
            {
                key: 'repair_request', title: '수리 요청 및 콜', desc: '정비소 수리 요청 전송 및 정비소 콜 배정 시스템',
                icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
            },
            {
                key: 'repair_status', title: '정비 현황 조회', desc: '차량별 수리 요청 현황 및 진행 상태 실시간 모니터링',
                icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line></svg>
            }
        ]
    }
];

interface RoleManagementProps {
    onDirtyChange?: (isDirty: boolean) => void;
}

const RoleManagement: React.FC<RoleManagementProps> = ({ onDirtyChange }) => {
    const [roles, setRoles] = useState<Role[]>(defaultRoles);
    const [selectedRoleId, setSelectedRoleId] = useState<string>(roles[0].id);
    const [activeTabIdx, setActiveTabIdx] = useState(0);
    const [isDirty, setIsDirty] = useState(false);

    const updateDirty = (dirty: boolean) => {
        setIsDirty(dirty);
        if (onDirtyChange) onDirtyChange(dirty);
    };

    const activeRole = roles.find(r => r.id === selectedRoleId) || roles[0];
    const isMaster = activeRole.id === 'r1';

    const handleUpdateRole = (updatedRole: Role) => {
        setRoles(prev => prev.map(r => r.id === updatedRole.id ? updatedRole : r));
        updateDirty(true);
    };

    const handleCreateRole = () => {
        const newRole: Role = {
            id: `r${Date.now()}`,
            name: '새 권한 그룹',
            description: '새로운 시스템 권한 그룹에 대한 설명을 입력하세요.',
            userCount: 0,
            permissions: {
                management_fleet: { preset: 'none', actions: getNoneActions() },
                management_driver: { preset: 'none', actions: getNoneActions() },
                control_battery: { preset: 'none', actions: getNoneActions() },
                linkage_driving: { preset: 'none', actions: getNoneActions() },
                linkage_maintenance: { preset: 'none', actions: getNoneActions() },
                linkage_disposal: { preset: 'none', actions: getNoneActions() },
                repair_diagnosis: { preset: 'none', actions: getNoneActions() },
                repair_recommendation: { preset: 'none', actions: getNoneActions() },
                repair_request: { preset: 'none', actions: getNoneActions() },
                repair_status: { preset: 'none', actions: getNoneActions() }
            }
        };
        setRoles([...roles, newRole]);
        setSelectedRoleId(newRole.id);
    };

    const handleDeleteRole = () => {
        if (isMaster) return;
        if (activeRole.userCount > 0) {
            alert(`이 그룹에 속한 사용자(${activeRole.userCount}명)가 있어 즉시 삭제할 수 없습니다.\n먼저 사용자 메뉴에서 이들의 권한을 다른 그룹으로 변경해주세요.`);
            return;
        }
        if (window.confirm(`정말 '${activeRole.name}' 그룹을 삭제하시겠습니까?`)) {
            const newRoles = roles.filter(r => r.id !== activeRole.id);
            setRoles(newRoles);
            if (newRoles.length > 0) setSelectedRoleId(newRoles[0].id);
        }
    };

    const handleSave = () => {
        alert(`${activeRole.name} 그룹의 세부 권한이 저장되었습니다.\n(해당 스코프에 속한 ${activeRole.userCount}명의 계정에 즉시 설정이 반영됩니다.)`);
        updateDirty(false);
    };

    const handleRoleSelect = (id: string) => {
        if (id === selectedRoleId) return;
        if (isDirty) {
            if (window.confirm("저장하지 않은 변경사항이 있습니다. 화면을 이동하기 전 저장하시겠습니까?\n\n[확인] 자동 저장 후 그룹 이동\n[취소] 기존 화면 유지")) {
                handleSave();
                setSelectedRoleId(id);
            }
        } else {
            setSelectedRoleId(id);
        }
    };

    const activeGroupData = MODULE_GROUPS[activeTabIdx];

    return (
        <div className="admin-role-mgmt modern-layout" style={{ paddingTop: '0px' }}>
            <div className="role-mgmt-body">
                {/* Left Sidebar */}
                <aside className="role-list-sidebar">
                    <div className="role-list-header">
                        <h3>접근 권한 그룹 목록</h3>
                        <button className="btn-list-add" onClick={handleCreateRole}>+ 새 그룹</button>
                    </div>
                    <div className="role-list-content">
                        {roles.map(role => (
                            <div 
                                key={role.id} 
                                className={`role-list-item ${selectedRoleId === role.id ? 'selected' : ''}`}
                                onClick={() => handleRoleSelect(role.id)}
                            >
                                <div className="role-item-title">
                                    <span className="role-item-name">{role.name}</span>
                                    <span className="role-item-count">{role.userCount}명</span>
                                </div>
                                <div className="role-item-desc">{role.description}</div>
                            </div>
                        ))}
                    </div>
                </aside>

                {/* Right Detail Panel */}
                <main className="role-detail-area">
                    <div className="role-detail-header" style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div className="role-info-edit" style={{ flex: 1 }}>
                            <input 
                                type="text" 
                                className="role-name-input" 
                                value={activeRole.name}
                                onChange={e => handleUpdateRole({ ...activeRole, name: e.target.value })}
                                disabled={isMaster}
                            />
                            <textarea 
                                className="role-desc-input" 
                                rows={2}
                                value={activeRole.description}
                                onChange={e => handleUpdateRole({ ...activeRole, description: e.target.value })}
                                disabled={isMaster}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '8px', marginLeft: '16px', marginTop: '4px' }}>
                            {!isMaster && (
                                <button className="btn-delete-role" onClick={handleDeleteRole}>그룹 삭제</button>
                            )}
                            <button className="btn-save-global" onClick={handleSave}>설정 저장</button>
                        </div>
                    </div>

                    {/* Dynamic Tabs placed inside right panel */}
                    <div className="role-module-tabs">
                        {MODULE_GROUPS.map((group, idx) => (
                            <button 
                                key={idx} 
                                className={`role-tab-btn ${activeTabIdx === idx ? 'active' : ''}`}
                                onClick={() => setActiveTabIdx(idx)}
                            >
                                {group.tabName}
                            </button>
                        ))}
                    </div>

                    <div className="role-detail-content" style={{ position: 'relative' }}>
                        {isMaster && (
                            <div className="master-role-overlay">
                                <div className="master-role-message">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#eab308" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path></svg>
                                    시스템 기본 관리자 그룹은 권한을 변경할 수 없습니다. 안전한 플랫폼 운영을 위해 모든 권한이 고정되어 있습니다.
                                </div>
                            </div>
                        )}

                        {activeGroupData.subModules.map(subMod => (
                            <ModulePermissionCard 
                                key={subMod.key}
                                activeRole={activeRole} 
                                moduleKey={subMod.key as keyof Role['permissions']} 
                                title={subMod.title} 
                                desc={subMod.desc}
                                icon={subMod.icon}
                                onChange={handleUpdateRole}
                                disabled={isMaster}
                            />
                        ))}
                    </div>
                </main>
            </div>
        </div>
    );
};

// Sub-component for each sub-module section
function ModulePermissionCard({ activeRole, moduleKey, title, desc, icon, onChange, disabled }: any) {
    const [isExpanded, setIsExpanded] = useState(false);
    const moduleData: SubMenuPermission = activeRole.permissions[moduleKey] || { preset: 'none', actions: getNoneActions() };

    const handlePresetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const val = e.target.value as PermissionPreset;
        let actions = { ...moduleData.actions };
        
        switch(val) {
            case 'full': actions = getFullActions(); break;
            case 'operate': actions = getOperateActions(); break;
            case 'view': actions = getViewActions(); break;
            case 'none': actions = getNoneActions(); break;
        }

        const newRole = {
            ...activeRole,
            permissions: {
                ...activeRole.permissions,
                [moduleKey]: { preset: val, actions }
            }
        };
        onChange(newRole);
    };

    const getPresetClass = () => `module-preset-select preset-${moduleData.preset}`;

    return (
        <div className="role-module-card" style={{ marginBottom: '12px' }}>
            <div className={`module-card-header ${isExpanded ? 'expanded' : ''}`} style={{ borderBottom: isExpanded ? '1px solid #e2e8f0' : 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' }} onClick={(e) => {
                // Prevent toggle if clicking on the select box
                if ((e.target as HTMLElement).tagName !== 'SELECT') {
                    setIsExpanded(!isExpanded);
                }
            }}>
                <div className="module-title-wrap" style={{ flex: 1 }}>
                    <div className="module-icon">
                        {icon}
                    </div>
                    <div>
                        <div className="module-name">{title}</div>
                        <div className="module-desc">{desc}</div>
                    </div>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <select className={getPresetClass()} value={moduleData.preset} onChange={handlePresetChange} disabled={disabled} onClick={(e) => e.stopPropagation()}>
                        <option value="none">접근 차단</option>
                        <option value="view">조회 전용</option>
                        <option value="operate">일반 권한</option>
                        <option value="full">최고 권한</option>
                    </select>
                    
                    <button 
                        className="btn-toggle-expand" 
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', color: '#64748b', display: 'flex', alignItems: 'center' }}
                    >
                        {isExpanded ? (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="18 15 12 9 6 15"></polyline></svg>
                        ) : (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
                        )}
                    </button>
                </div>
            </div>
            
            {moduleData.preset !== 'none' && isExpanded && (
                <div className="module-card-body" style={{ padding: '20px', backgroundColor: '#f8fafc', borderTop: 'none', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px' }}>
                    <div className="advanced-actions-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px' }}>
                        <ActionCard label="목록 보기" desc="메뉴에 진입하여 리스트 형태의 데이터를 열람합니다." allowed={moduleData.actions.readList} />
                        <ActionCard label="상세정보 보기" desc="특정 항목을 클릭하여 가려진 세부 내용을 열람합니다." allowed={moduleData.actions.readDetail} />
                        
                        <ActionCard label="신규 등록" desc="사용자나 데이터를 시스템에 새롭게 추가합니다." allowed={moduleData.actions.create} />
                        <ActionCard label="정보 수정" desc="기존에 입력되어 있는 데이터의 내용을 변경합니다." allowed={moduleData.actions.update} />
                        
                        <ActionCard danger label="데이터 삭제" desc="등록된 항목을 지웁니다. 주의를 요구하는 권한입니다." allowed={moduleData.actions.delete} />
                        <ActionCard danger label="파일 다운로드" desc="민감한 데이터를 엑셀 등 파일 형태로 다운로드합니다." allowed={moduleData.actions.export} />
                        
                        {moduleKey === 'linkage_driving' && (
                            <ActionCard label="이메일 전송" desc="요약본을 정기적으로 수신자에게 전송합니다." allowed={!!moduleData.actions.manageEmails} />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function ActionCard({ label, desc, allowed, danger }: any) {
    return (
        <div style={{ 
            display: 'flex', 
            alignItems: 'flex-start', 
            padding: '14px', 
            backgroundColor: allowed ? '#ffffff' : '#f1f5f9',
            border: allowed ? '1px solid #cbd5e1' : '1px solid #e2e8f0',
            borderRadius: '8px',
            opacity: allowed ? 1 : 0.65,
            boxShadow: allowed ? '0 1px 2px rgba(0,0,0,0.02)' : 'none'
        }}>
            <div style={{ marginTop: '2px', marginRight: '12px', flexShrink: 0 }}>
                {allowed ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', backgroundColor: '#eff6ff', borderRadius: '50%', color: '#2563eb' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                    </div>
                ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', backgroundColor: '#e2e8f0', borderRadius: '50%', color: '#94a3b8' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                    </div>
                )}
            </div>
            <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                    <span style={{ fontWeight: 600, fontSize: '13px', color: allowed ? (danger ? '#dc2626' : '#334155') : '#94a3b8', textDecoration: allowed ? 'none' : 'line-through' }}>{label}</span>
                </div>
                <div style={{ fontSize: '12px', color: allowed ? '#64748b' : '#94a3b8', lineHeight: '1.4' }}>{desc}</div>
            </div>
        </div>
    );
}

export default RoleManagement;

